import { useCallback, useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  BROWSER_TRACKER_UI_CAPABILITIES,
  OntologyInspector,
  TrackersUIProvider,
  useTrackerCommand,
  useTrackerDataSelector,
  type OntologyInspectorWriter,
} from '@nimbalyst/collab-bundle/trackers-ui';
import { CollabLoadingPanel } from '../components/Chrome';
import { getProjectRouteId } from '../projectSelection';
import { PublicGraphSettingsPanel } from '../publicWiki/PublicGraphSettings';
import { orgPageItemPath, orgTrackerItemPath, orgTrackerSetupPath, orgTrackersPath, orgWikiTypesPath, type RouteTeam, type TrackerSetupTab } from '../routing';
import { useProjectTrackerSession } from '../trackers/ProjectTrackerSession';
import { TrackersAccessGate } from '../trackers/TrackersShell';
import type { BrowserTrackerSession } from '../trackers/useBrowserTrackerSession';
import { DocsSurfaceError } from './DocsRoutes';
import type { OrganizationRouteContext } from './workspaceContext';

/** Knowledge items open as wiki pages; everything else opens beside its tracker list. */
const WIKI_TYPES: ReadonlySet<string> = new Set(['entity', 'claim']);

/**
 * The project's Tracker setup screen: the Ontology inspector (admin plumbing:
 * every tracker type, its fields and fill rates, and the gaps between them)
 * and whether the project's wiki is public. Labels, their type map and their
 * proposals are browsed in the wiki's Types section, which the inspector
 * links to.
 *
 * Reads the same room Trackers and the wiki read (`ProjectTrackerSession`), in
 * its own provider rather than inside the Trackers frame: setup is a page about
 * the trackers, not a list pane beside them.
 */
export function TrackerSetupRoute() {
  const context = useOutletContext<OrganizationRouteContext>();
  const { projectId, tab } = useParams<{ projectId?: string; tab?: string }>();
  const { session, identity, rosterFailed, rosterResolved } = useProjectTrackerSession();

  if (!context.project) {
    return <DocsSurfaceError kind="no-access" detail="This organization has no project available for Trackers." />;
  }
  const selectedProjectId = getProjectRouteId(context.project);
  if (!projectId || projectId !== selectedProjectId || (tab && tab !== 'ontology' && tab !== 'public')) {
    return <Navigate to={orgTrackerSetupPath(context.team, selectedProjectId)} replace />;
  }
  if (rosterFailed) {
    return (
      <DocsSurfaceError
        kind="service-unavailable"
        detail="The organization roster could not be loaded, so this browser cannot say who you are in this team."
      />
    );
  }
  if (rosterResolved && !identity) {
    return (
      <DocsSurfaceError
        kind="removed-member"
        detail="You are not listed as a member of this organization, so its tracker setup cannot be opened here."
      />
    );
  }
  if (!session) {
    return <CollabLoadingPanel title="Opening tracker setup" detail="Connecting to your team’s live tracker room." />;
  }
  return (
    <TrackersUIProvider dataSource={session.dataSource} identity={identity} capabilities={BROWSER_TRACKER_UI_CAPABILITIES}>
      <TrackersAccessGate team={context.team}>
        <TrackerSetupScreen
          team={context.team}
          projectName={context.project.name ?? 'Project'}
          projectId={selectedProjectId}
          teamProjectId={context.project.teamProjectId}
          tab={tab === 'public' ? 'public' : 'ontology'}
          getTeamJwt={context.getTeamJwt}
          session={session}
          actor={identity?.email ?? null}
        />
      </TrackersAccessGate>
    </TrackersUIProvider>
  );
}

function TrackerSetupScreen({ team, projectName, projectId, teamProjectId, tab, getTeamJwt, session, actor }: {
  team: RouteTeam & Pick<OrganizationRouteContext['team'], 'orgId' | 'role'>;
  projectName: string;
  projectId: string;
  teamProjectId: string;
  tab: TrackerSetupTab;
  getTeamJwt: OrganizationRouteContext['getTeamJwt'];
  session: BrowserTrackerSession;
  actor: string | null;
}) {
  const navigate = useNavigate();
  const records = useTrackerDataSelector((state) => state.records);
  const recordsById = useTrackerDataSelector((state) => state.recordsById);
  const loaded = useTrackerDataSelector((state) => state.loaded);
  const command = useTrackerCommand();
  // The team registry is not published to every room yet; an empty list means
  // "unknown" here, not "declares nothing", so the inspector keys predicates
  // off the ids claims use instead of calling every one undeclared.
  const predicates = session.schema.predicates.length ? session.schema.predicates : null;
  const writer = useMemo<OntologyInspectorWriter>(() => ({ command, workspace: projectId, actor }), [command, projectId, actor]);
  const openItem = useCallback((itemId: string) => {
    const type = recordsById.get(itemId)?.primaryType;
    navigate(type && WIKI_TYPES.has(type)
      ? orgPageItemPath(team, projectId, itemId)
      : orgTrackerItemPath(team, projectId, itemId, { trackerType: type ?? null }));
  }, [navigate, recordsById, team, projectId]);

  useEffect(() => {
    document.title = `${tab === 'public' ? 'Public wiki' : 'Ontology'} · Tracker setup · Nimbalyst`;
  }, [tab]);
  const tabButton = (id: TrackerSetupTab, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === id}
      className={tab === id
        ? 'cursor-pointer border-0 border-b-2 border-solid border-[var(--nim-primary)] bg-transparent px-3 py-2 text-[13px] text-nim'
        : 'cursor-pointer border-0 border-b-2 border-solid border-transparent bg-transparent px-3 py-2 text-[13px] text-nim-faint hover:text-nim'}
      onClick={() => navigate(orgTrackerSetupPath(team, projectId, id === 'ontology' ? undefined : id))}
    >
      {label}
    </button>
  );

  return (
    <section className="tracker-setup flex h-full min-h-0 w-full flex-col overflow-hidden bg-nim">
      <header className="tracker-setup-header flex shrink-0 flex-col gap-1 px-6 pt-3">
        <nav className="flex items-center gap-2 text-[13px] text-nim-faint" aria-label="Breadcrumb">
          <span>{projectName}</span>
          <span>/</span>
          <button
            type="button"
            className="cursor-pointer border-none bg-transparent p-0 text-nim-faint hover:text-nim hover:underline"
            onClick={() => navigate(orgTrackersPath(team, projectId))}
          >
            Trackers
          </button>
          <span>/</span>
          <span className="text-nim">Tracker setup</span>
        </nav>
        <div className="flex items-end gap-1" role="tablist">
          {tabButton('ontology', 'Ontology')}
          {tabButton('public', 'Public')}
        </div>
      </header>
      <div className="tracker-setup-body flex min-h-0 flex-1 overflow-auto border-t border-nim select-text">
        {tab === 'public' ? (
          <PublicGraphSettingsPanel
            orgId={team.orgId}
            teamProjectId={teamProjectId}
            projectName={projectName}
            isAdmin={team.role === 'owner' || team.role === 'admin'}
            getTeamJwt={getTeamJwt}
          />
        ) : (
          <OntologyInspector
            types={session.schema.trackerTypes}
            predicates={predicates}
            labels={session.schema.labels}
            onOpenTypes={() => navigate(orgWikiTypesPath(team, projectId))}
            records={records}
            loaded={loaded}
            writer={writer}
            onOpenItem={openItem}
          />
        )}
      </div>
    </section>
  );
}
